import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { stockAPI } from '../../../api';
import Layout from '../../../components/Layout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Card, CardContent } from '../../../components/ui/Card';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { useToast } from '../../../hooks/useToast';
import {
  STOCK_UNIT_TYPES,
  STOCK_CATEGORIES,
  MOVEMENT_TYPES,
  labelFor,
  formatQuantity,
} from '../../../lib/stockConstants';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500';
const selectClass =
  'flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const emptyForm = {
  itemName: '',
  sku: '',
  category: '',
  description: '',
  supplierName: '',
  manufacturer: '',
  unitType: 'each',
  quantityOnHand: '',
  minimumStockLevel: '',
  reorderQuantity: '',
  storageLocation: '',
  latestCost: '',
  vatPaid: '',
};

const StockDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const { success, error: toastError, ToastContainer } = useToast();

  const [item, setItem] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  useEffect(() => {
    if (!isNew) {
      loadItem();
      loadMovements();
    }
  }, [id]);

  const loadItem = async () => {
    try {
      setLoading(true);
      const res = await stockAPI.getOne(id);
      setItem(res.item);
      setForm({
        itemName: res.item.itemName || '',
        sku: res.item.sku || '',
        category: res.item.category || '',
        description: res.item.description || '',
        supplierName: res.item.supplierName || '',
        manufacturer: res.item.manufacturer || '',
        unitType: res.item.unitType || 'each',
        quantityOnHand: '',
        minimumStockLevel: res.item.minimumStockLevel ?? '',
        reorderQuantity: res.item.reorderQuantity ?? '',
        storageLocation: res.item.storageLocation || '',
        latestCost: res.item.latestCost ?? '',
        vatPaid: res.item.vatPaid ?? '',
      });
    } catch (err) {
      toastError(err.message || 'Failed to load stock item');
    } finally {
      setLoading(false);
    }
  };

  const loadMovements = async () => {
    try {
      const res = await stockAPI.getMovements(id);
      setMovements(res.movements || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.itemName.trim()) {
      toastError('Item name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        itemName: form.itemName.trim(),
        sku: form.sku.trim() || null,
        category: form.category || null,
        description: form.description.trim() || null,
        supplierName: form.supplierName.trim() || null,
        manufacturer: form.manufacturer.trim() || null,
        unitType: form.unitType,
        minimumStockLevel: form.minimumStockLevel !== '' ? parseFloat(form.minimumStockLevel) : null,
        reorderQuantity: form.reorderQuantity !== '' ? parseFloat(form.reorderQuantity) : null,
        storageLocation: form.storageLocation.trim() || null,
        latestCost: form.latestCost !== '' ? parseFloat(form.latestCost) : null,
        vatPaid: form.vatPaid !== '' ? parseFloat(form.vatPaid) : null,
      };

      if (isNew) {
        const initialQty = form.quantityOnHand !== '' ? parseFloat(form.quantityOnHand) : 0;
        const res = await stockAPI.create(payload);
        if (initialQty > 0) {
          await stockAPI.addMovement(res.item.id, {
            quantityChange: initialQty,
            movementType: 'purchase',
            notes: 'Initial stock on create',
          });
        }
        success('Stock item created');
        navigate(`/tenant/stock/${res.item.id}`);
      } else {
        await stockAPI.update(id, payload);
        success('Stock item updated');
        loadItem();
      }
    } catch (err) {
      toastError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustment = async (e) => {
    e.preventDefault();
    const qty = parseFloat(adjustQty);
    if (!Number.isFinite(qty) || qty === 0) {
      toastError('Enter a non-zero quantity change (+ to add, − to remove)');
      return;
    }
    setAdjusting(true);
    try {
      await stockAPI.addMovement(id, {
        quantityChange: qty,
        movementType: 'adjustment',
        notes: adjustNotes.trim() || null,
      });
      success('Stock adjusted');
      setAdjustQty('');
      setAdjustNotes('');
      loadItem();
      loadMovements();
    } catch (err) {
      toastError(err.message || 'Adjustment failed');
    } finally {
      setAdjusting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8 text-gray-600 dark:text-gray-400">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ToastContainer />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-h1 font-medium text-gray-900 dark:text-gray-100">
              {isNew ? 'New stock item' : item?.itemName}
            </h1>
            {!isNew && item?.isLowStock && (
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                Below minimum stock level
              </p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/tenant/stock')}>
            ← Back to stock
          </Button>
        </div>

        <Card className="mb-6 bg-white dark:bg-gray-800">
          <CardContent className="p-6">
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <FormLabel className="text-gray-700 dark:text-gray-300">Item name *</FormLabel>
                  <Input
                    value={form.itemName}
                    onChange={(e) => handleChange('itemName', e.target.value)}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">SKU</FormLabel>
                  <Input
                    value={form.sku}
                    onChange={(e) => handleChange('sku', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Category</FormLabel>
                  <select
                    value={form.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    className={selectClass}
                  >
                    <option value="">—</option>
                    {STOCK_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Unit type</FormLabel>
                  <select
                    value={form.unitType}
                    onChange={(e) => handleChange('unitType', e.target.value)}
                    className={selectClass}
                  >
                    {STOCK_UNIT_TYPES.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </div>
                {isNew && (
                  <div>
                    <FormLabel className="text-gray-700 dark:text-gray-300">Initial quantity</FormLabel>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={form.quantityOnHand}
                      onChange={(e) => handleChange('quantityOnHand', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                )}
                {!isNew && (
                  <div>
                    <FormLabel className="text-gray-700 dark:text-gray-300">On hand</FormLabel>
                    <p className="text-lg font-medium text-gray-900 dark:text-gray-100 py-2">
                      {formatQuantity(item?.quantityOnHand, item?.unitType)}
                    </p>
                  </div>
                )}
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Minimum level</FormLabel>
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    value={form.minimumStockLevel}
                    onChange={(e) => handleChange('minimumStockLevel', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Storage location</FormLabel>
                  <Input
                    value={form.storageLocation}
                    onChange={(e) => handleChange('storageLocation', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Supplier name</FormLabel>
                  <Input
                    value={form.supplierName}
                    onChange={(e) => handleChange('supplierName', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Latest cost (£)</FormLabel>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.latestCost}
                    onChange={(e) => handleChange('latestCost', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? 'Saving...' : isNew ? 'Create item' : 'Save changes'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {!isNew && (
          <>
            <Card className="mb-6 bg-white dark:bg-gray-800">
              <CardContent className="p-6">
                <h2 className="text-h3 font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Adjust quantity
                </h2>
                <form onSubmit={handleAdjustment} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <FormLabel className="text-gray-700 dark:text-gray-300">
                      Change (+ add / − remove)
                    </FormLabel>
                    <Input
                      type="number"
                      step="any"
                      value={adjustQty}
                      onChange={(e) => setAdjustQty(e.target.value)}
                      placeholder="e.g. 10 or -2"
                      className={inputClass}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <FormLabel className="text-gray-700 dark:text-gray-300">Notes</FormLabel>
                    <Input
                      value={adjustNotes}
                      onChange={(e) => setAdjustNotes(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <Button type="submit" variant="outline" disabled={adjusting}>
                      {adjusting ? 'Applying...' : 'Apply adjustment'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card className="bg-white dark:bg-gray-800">
              <CardContent className="p-6">
                <h2 className="text-h3 font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Movement history
                </h2>
                {movements.length === 0 ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">No movements yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Change</TableHead>
                        <TableHead>Balance</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {movements.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell>
                            {new Date(m.createdAt).toLocaleDateString('en-GB')}
                          </TableCell>
                          <TableCell>{labelFor(MOVEMENT_TYPES, m.movementType)}</TableCell>
                          <TableCell
                            className={
                              m.quantityChange >= 0
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            }
                          >
                            {m.quantityChange >= 0 ? '+' : ''}
                            {m.quantityChange}
                          </TableCell>
                          <TableCell>{m.newQuantity}</TableCell>
                          <TableCell className="text-gray-600 dark:text-gray-400">
                            {m.notes || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default StockDetail;
